export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiGet<ResponseBody>(path: string): Promise<ResponseBody> {
  return apiRequest<ResponseBody>(path, {
    method: "GET",
  });
}

export async function apiPost<RequestBody, ResponseBody>(path: string, body: RequestBody): Promise<ResponseBody> {
  return apiRequest<ResponseBody>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiPatch<RequestBody, ResponseBody>(path: string, body: RequestBody): Promise<ResponseBody> {
  return apiRequest<ResponseBody>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function apiDelete<ResponseBody>(path: string): Promise<ResponseBody> {
  return apiRequest<ResponseBody>(path, {
    method: "DELETE",
  });
}

async function apiRequest<ResponseBody>(path: string, init: RequestInit): Promise<ResponseBody> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...init.headers,
    },
  });
  const payload = await readJson(response);

  if (!response.ok) {
    throw new ApiError(getErrorMessage(payload, response.status), response.status, payload);
  }

  if (typeof payload === "string") {
    throw new ApiError("Unexpected API response.", response.status, payload);
  }

  return payload as ResponseBody;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getErrorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string") {
    return payload.error;
  }

  return `Request failed with status ${status}.`;
}
